import React, { useEffect, useMemo, useState } from 'react'
import type { MouseEvent, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { List, ListItem, Tooltip } from 'flipper-ui'
import {
    IconAssignment,
    IconCheckBox,
    IconDateRange,
    IconHome
} from 'flipper-ui/icons/mui'
import { IconIPE } from 'flipper-ui/icons/specific/IconIPE'
import { IconRisks } from 'flipper-ui/icons/specific/IconRisks'
import type { TFunction } from 'i18next'
import { FlipperSidebar, Link, PaperBar } from './styles'

interface ISidebarOption {
    icon: ReactElement
    label: string
    name: string
    route: string
}

const getOptions = (t: TFunction) => [
    {
        icon: <IconHome />,
        label: t('common:home'),
        name: 'home',
        route: '/home'
    },
    {
        icon: <IconDateRange />,
        label: t('portal:consultations:moduleLabel'),
        name: 'consultations',
        route: '/consultations'
    },
    {
        icon: <IconIPE width={24} height={24} />,
        label: t('portal:ppe:moduleLabel'),
        name: 'ppe',
        route: '/epis'
    },
    {
        icon: <IconRisks width={24} height={24} />,
        label: t('portal:risks:moduleLabel'),
        name: 'risks',
        route: '/risks'
    },
    {
        icon: <IconCheckBox />,
        label: t('portal:safetyQuiz:moduleLabel'),
        name: 'safety-questionnaires',
        route: '/safety-questionnaires'
    },
    {
        icon: <IconAssignment />,
        label: t('portal:anamnesis:moduleLabel'),
        name: 'clinical-questionnaires',
        route: '/clinical-questionnaires'
    }
]

export const Sidebar = () => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()

    const [selected, setSelected] = useState<string>('')
    const [expanded, setExpanded] = useState<boolean>(true)

    const options = useMemo(() => getOptions(t), [t])

    useEffect(() => {
        const currentRoute = location.pathname

        const matchedOption = options.find(
            option => option.route === currentRoute
        )

        if (matchedOption && matchedOption.name !== selected) {
            setSelected(matchedOption.name)
        }
    }, [location.pathname, selected])

    const selectedOption = options.find(
        ({ name, route }) =>
            location.pathname.includes(name) || location.pathname === route
    )

    const handleToggleSidebar = () => {
        setExpanded(prev => !prev)
    }

    const handleGoTo = (name: string, route: string) => () => {
        setSelected(name)
        navigate(route)
    }

    const handleCancelClick = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault()
        event.stopPropagation()
    }

    const renderOption = (option: ISidebarOption) => (
        <Link key={option.name} href={option.route} onClick={handleCancelClick}>
            <Tooltip
                withWrapper
                enterDelay={500}
                placement='right'
                title={expanded ? '' : option.label}>
                <ListItem
                    key={option.name}
                    icon={option.icon}
                    subtitle={expanded ? option.label : ''}
                    selected={selectedOption && option.name === selected}
                    onClick={handleGoTo(option.name, option.route)}
                />
            </Tooltip>
        </Link>
    )

    return (
        <FlipperSidebar
            open
            docked
            top={56}
            id='sidebar'
            name='sidebar'
            minWidth={56}
            maxWidth={224}
            expanded={expanded}
            onToggle={handleToggleSidebar}>
            <PaperBar>
                <List dense>{options.map(renderOption)}</List>
            </PaperBar>
        </FlipperSidebar>
    )
}
